import Cadence, {
  CadenceType,
  dateDayToCadenceDayOfWeek,
  Qualifier,
  qualifierIntToQualifier,
} from '@repo/utils/models/Cadence';
import * as chrono from 'chrono-node';

type ParsedCadence = { cadence: Cadence | null; cadenceType: CadenceType | null };

const cadenceTypePatterns: Array<[CadenceType, RegExp]> = [
  ['weekly', /wee/i],
  ['monthly', /mont/i],
  ['quarterly', /qu/i],
  ['yearly', /ye/i],
  ['auto', /aut/i],
  ['none', /(non|di)/i],
];

const ordinalPatterns: Array<[number, RegExp]> = [
  [1, /first|1st/i],
  [2, /second|2nd/i],
  [3, /third|3rd/i],
  [4, /fourth|4th/i],
  [5, /fifth|5th/i],
  [6, /sixth|6th/i],
  [7, /seventh|7th/i],
  [8, /eighth|8th/i],
  [9, /ninth|9th/i],
  [10, /tenth|10th/i],
  [11, /eleventh|11th/i],
  [12, /twelfth|12th/i],
  [13, /thirteenth|13th/i],
  [14, /fourteenth|14th/i],
  [15, /fifteenth|15th/i],
  [16, /sixteenth|16th/i],
  [17, /seventeenth|17th/i],
  [18, /eighteenth|18th/i],
  [19, /nineteenth|19th/i],
  [20, /twentieth|20th/i],
  [21, /twenty-?first|21st/i],
  [22, /twenty-?second|22nd/i],
  [23, /twenty-?third|23rd/i],
  [24, /twenty-?fourth|24th/i],
  [25, /twenty-?fifth|25th/i],
  [26, /twenty-?sixth|26th/i],
  [27, /twenty-?seventh|27th/i],
  [28, /twenty-?eighth|28th/i],
  [29, /twenty-?ninth|29th/i],
  [30, /thirtieth|30th/i],
  [31, /thirty-?first|31st/i],
  [-1, /last/i],
];

const createPatternParser =
  (patterns: Array<[any, RegExp]>) =>
  (input: string): any => {
    for (let i = 0; i < patterns.length; i++) {
      const [type, regex] = patterns[i];
      if (regex.test(input)) {
        return type;
      }
    }

    return null;
  };

const parseForCadenceType = createPatternParser(cadenceTypePatterns);
const parseForOrdinal = createPatternParser(ordinalPatterns);

const parsedDayBasedCadence = (ordinal: number, cadenceType?: CadenceType): ParsedCadence => ({
  cadence: new Cadence({ type: 'none', dayOfMonth: ordinal }),
  cadenceType,
});

const parsedDateBasedCadence = (
  input: string,
  date: Date,
  qualifier: Qualifier,
  cadenceType: CadenceType,
): ParsedCadence => {
  let dayOfWeek = dateDayToCadenceDayOfWeek(date.getDay());
  let dayOfMonth = date.getDate();
  const month = date.getMonth() + 1;
  const parts = chrono.parse(input, new Date(), { forwardDate: true });

  if (parts && parts.length) {
    const parsedDate = parts[0].start;
    if (parsedDate.isCertain('month') && parsedDate.isCertain('day')) {
      cadenceType = 'yearly';
    }
  }

  return {
    cadence: new Cadence({ type: 'none', dayOfWeek, dayOfMonth, month, qualifier }),
    cadenceType,
  };
};

// TODO: consider extending this further with custom chrono parsing for holidays, etc.
export default function parseCadence(input: string): ParsedCadence {
  let cadence = null;
  let cadenceType = parseForCadenceType(input);
  const ordinal = parseForOrdinal(input);
  const qualifier = qualifierIntToQualifier[ordinal];
  const dayIsSpecified = / day/i.test(input);

  if (dayIsSpecified && ordinal !== null && ordinal !== -1) {
    return parsedDayBasedCadence(ordinal, cadenceType);
  }

  const parsedDate = chrono.parseDate(input, new Date(), { forwardDate: true });

  if (parsedDate) {
    return parsedDateBasedCadence(input, parsedDate, qualifier, cadenceType);
  }

  return { cadence, cadenceType };
}
